import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, catchError, map, Observable, of, switchMap, tap, throwError, timeout} from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {UserLoginDto} from './UserLoginDto';
import {UserRegistrationDto} from './UserRegistrationDto';
import {environment} from '../../../environments/environment';
import {LocalStorageService} from './local-storage.service';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {GclidService} from './gclid.service';
import {SignupSourceService} from './signup-source.service';
import {isJwtExpired} from './auth/jwt.util';
import {UserBadgeDto} from './UserBadgeDto';
import {notionTrackerUrl} from '../utils/notion-tracker-url';

export type OauthFailureReason = 'NO_DATABASE_FOUND' | 'INVALID_SCHEMA' | 'MULTIPLE_DATA_SOURCES' | 'DATABASE_UNREACHABLE' | 'ACCESS_REVOKED' | 'UPSTREAM_NOTION_UNAVAILABLE' | 'CONNECT_ERROR';

/**
 * `UserEntity.trackingMode` (backend backlog #366). `GET /user/me` always
 * sends the key when the account exists; a row predating the column reads
 * as NOTION on the backend.
 */
export type TrackingMode = 'NOTION' | 'NATIVE';

/** Response of `GET /user/widget-token` (the `notionUserId` param is optional since backend #366). */
export interface WidgetTokenResponse {
  widgetToken: string;
  /** Absolute widget URL with the token in the `#token=` fragment. */
  widgetUrl: string;
}

/** 200 body of `POST /user/tracking-mode` (backend backlog #366). */
export interface TrackingModeResponse {
  trackingMode: TrackingMode;
  widgetToken: string;
}

/**
 * Typed body of `GET /user/me`. A NATIVE account returns `widgetToken`
 * without `notionUserId`; an unconnected NOTION account returns neither.
 */
export interface UserMeResponse {
  notionUserId: string;
  widgetToken: string;
  hasPassword?: string;
  trackingMode?: TrackingMode;
}

export interface OauthStatus {
  connected: boolean;
  databaseId: string | null;
  error: boolean;
  reason: OauthFailureReason | null;
  issues: string[] | null;
  /**
   * Backlog #323, additive: true when the connect flow patched the widget
   * embed into the user's duplicated template, false when the user has to
   * embed it by hand, null when not connected. Optional because older
   * backends omit the key entirely; `undefined` means "unknown", which the
   * /start card renders as its neutral state.
   */
  embedPlaced?: boolean | null;
  /**
   * Additive since backend fix/embed-skip-reason: why the widget embed was not
   * placed. Always null unless embedPlaced is false, then one of
   * NO_PARENT_PAGE (the connected database has no parent page, e.g. a
   * workspace top-level database, so there was no page to place the widget
   * into), NO_EMBED_BLOCK (the page has no empty embed block and no widget
   * embed to patch), CHILDREN_UNAVAILABLE (the page's blocks could not be
   * read) or PATCH_FAILED (the Notion PATCH failed). Optional because older
   * backends omit the key. The type keeps `string` so unknown future values
   * still compile; the /start card renders them with the generic copy.
   */
  embedSkipReason?: 'NO_PARENT_PAGE' | 'NO_EMBED_BLOCK' | 'CHILDREN_UNAVAILABLE' | 'PATCH_FAILED' | string | null;
  /**
   * Additive: id of the Notion page that contains the connected habit
   * database (the "Habit Heroes" page), null when unknown (no parent page,
   * lookup failed, older connect). Optional because older backends omit the
   * key; absent is treated like null. Feeds the /start "Open your Habit
   * Tracker" link, which falls back to the database id when this is empty.
   */
  trackerPageId?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  public redirectUrl: string | null = null;

  private loggedIn = new BehaviorSubject<boolean>(false);

  private _loggedInEmail = new BehaviorSubject<string>('');

  constructor(private http: HttpClient,
              private storageService: LocalStorageService,
              private router: Router,
              @Inject(PLATFORM_ID) private platformId: Object,
              private gclidService: GclidService,
              private signupSourceService: SignupSourceService) {
    const token = this.storageService.getItem('token');
    const isBrowser = isPlatformBrowser(this.platformId);
    const hasLiveSession = isBrowser && !isJwtExpired(token);
    this.loggedIn = new BehaviorSubject<boolean>(hasLiveSession);
    if (isBrowser && token && !hasLiveSession) {
      this.clearSession();
    }
  }

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  setLoggedIn(value: boolean) {
    this.loggedIn.next(value);
  }

  get loggedInEmail(): BehaviorSubject<string> {
    return this._loggedInEmail;
  }

  setLoggedInEmail(value: string) {
    this._loggedInEmail.next(value);
  }

  loginWithGoogle(credential: string, newsletter?: boolean): Observable<HttpResponse<any>> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const gclid = this.gclidService.getValidGclid();
    const signupSource = this.signupSourceService.getValidSource();
    const body: Record<string, string | boolean> = {credential, timezone};
    // Opt-in only, mirrors register(...): the key is never sent as false.
    if (newsletter === true) {
      body['newsletter'] = true;
    }
    if (gclid) {
      body['gclid'] = gclid;
      // gclid is not cleared here: the response is text (no created field surfaced),
      // so we cannot determine whether this was a new-account creation.
    }
    if (signupSource) {
      if (signupSource.referrer) {
        body['signupReferrer'] = signupSource.referrer;
      }
      if (signupSource.utmSource) {
        body['utmSource'] = signupSource.utmSource;
      }
      if (signupSource.utmMedium) {
        body['utmMedium'] = signupSource.utmMedium;
      }
      if (signupSource.utmCampaign) {
        body['utmCampaign'] = signupSource.utmCampaign;
      }
      // signupSource is not cleared here, mirroring gclid above.
    }

    return this.http.post(`${environment.apiURL}/user/login/google`, body, { observe: 'response', responseType: 'text' }).pipe(
      tap((res: HttpResponse<any>) => {
        const token = res.headers.get('Authorization');
        this.storageService.setItem('token', token);
        const payload = JSON.parse(atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const email = payload.email;
        this.storageService.setItem('email', email);
        this.setLoggedIn(true);
        this.setLoggedInEmail(email);
      }),
      catchError((error) => {
        this.setLoggedIn(false);
        this.setLoggedInEmail('');
        return throwError(error);
      })
    );
  }

  login(email: string, password: string): Observable<HttpResponse<any>> {
    const body = new UserLoginDto(email, password);

    return this.http.post(`${environment.apiURL}/user/login`, body, {observe: 'response', responseType: 'text'}).pipe(
      tap((res: HttpResponse<any>) => {
        const token = res.headers.get('Authorization');
        this.storageService.setItem('token', token);
        this.storageService.setItem('email', email);
        this.setLoggedIn(true);
        this.setLoggedInEmail(email);
      }),
      catchError((error) => {
        this.setLoggedIn(false);
        this.setLoggedInEmail('');
        return throwError(error);
      })
    );
  }

  clearSession(): void {
    this.storageService.removeItem('token');
    this.storageService.removeItem('email');
    this.storageService.removeItem('notionUserId');
    this.storageService.removeItem('oauthProcessStarted');
    this.storageService.removeItem('widgetToken');
    this.storageService.removeItem('oauthDbFailed');
    this.storageService.removeItem('notionConn');

    this.setLoggedIn(false);
    this.setLoggedInEmail('');
  }

  logout() {
    const currentPath = this.router.url.split('?')[0].split('#')[0];
    // this.storageService.setItem('redirectAfterLogin', currentPath);

    this.clearSession();

    const isOnPrivatePage = this.router.url.startsWith('/competition') || this.router.url.startsWith('/start') || this.router.url.startsWith('/profile')
      || this.router.url.startsWith('/duel/join') || this.router.url.startsWith('/group/join') || this.router.url.startsWith('/groups');
    if (isOnPrivatePage) {
      this.router.navigate(['/login']);
    } else {
      this.router.navigate(['/']);
    }
  }

  register(email: string, username: string, password: string, postConfirmRedirect?: string,
           newsletter?: boolean): Observable<HttpResponse<any>> {
    const gclid = this.gclidService.getValidGclid() ?? undefined;
    const signupSource = this.signupSourceService.getValidSource();
    // An undefined newsletter is omitted from the posted JSON (JSON.stringify drops undefined properties).
    const body = new UserRegistrationDto(email, username, password, Intl.DateTimeFormat().resolvedOptions().timeZone, gclid,
      signupSource?.referrer, signupSource?.utmSource, signupSource?.utmMedium, signupSource?.utmCampaign, postConfirmRedirect, newsletter);

    return this.http.post(`${environment.apiURL}/user/register`, body, {observe: 'response', responseType: 'text'}).pipe(
      tap((response: HttpResponse<any>) => {
        this.gclidService.clear();
        this.signupSourceService.clear();
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  delete(email: string, password: string) {
    const body = new UserLoginDto(email, password);

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      observe: 'response' as 'response',
      responseType: 'text' as 'json',
      withCredentials: true,
    };

    return this.http.post(`${environment.apiURL}/user/delete`, body, options).pipe(
      tap(() => {
        this.setLoggedIn(false);
        this.setLoggedInEmail('');
        this.storageService.removeItem('token');
        this.storageService.removeItem('email');
        this.storageService.removeItem('notionConn');
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  resendConfirmation(email: string) {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      observe: 'response' as 'response',
      responseType: 'text' as 'text',
    };

    return this.http.post(`${environment.apiURL}/user/resend/confirmation?email=${encodeURIComponent(email)}`, {}, options).pipe(
      tap(() => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  newsletter(email: string): Observable<HttpResponse<string>> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      observe: 'response' as 'response',
      responseType: 'text' as 'text',
    };

    return this.http.post(`${environment.apiURL}/user/confirm-newsletter?email=${encodeURIComponent(email)}`, {}, options).pipe(
      tap(() => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  checkEmail(email: string) {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      observe: 'response' as 'response',
      responseType: 'text' as 'text',
    };

    return this.http.post(`${environment.apiURL}/user/check-email?email=${encodeURIComponent(email)}`, {}, options);
  }

  resendPassword(email: string) {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
      observe: 'response' as 'response',
      responseType: 'text' as 'text',
    };

    return this.http.post(`${environment.apiURL}/user/resend/password?email=${encodeURIComponent(email)}`, {}, options).pipe(
      tap(() => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  resetPassword(payload: { email: string; token: string; password: string }): Observable<any> {
    return this.http.post(`${environment.apiURL}/user/reset/password`, payload, {responseType: 'text'});
  }

  disconnectNotion(): Observable<{ disconnected: boolean }> {
    return this.http.post<{ disconnected: boolean }>(
      `${environment.apiURL}/user/notion/disconnect`,
      {}
    );
  }


  loginWithNotion(code: string): Observable<HttpResponse<{ notionUserId: string, widgetToken: string, created: boolean, connected?: boolean, email: string }>> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const gclid = this.gclidService.getValidGclid();
    const gclidParam = gclid ? `&gclid=${encodeURIComponent(gclid)}` : '';
    const signupSource = this.signupSourceService.getValidSource();
    let signupSourceParams = '';
    if (signupSource) {
      if (signupSource.referrer) {
        signupSourceParams += `&signupReferrer=${encodeURIComponent(signupSource.referrer)}`;
      }
      if (signupSource.utmSource) {
        signupSourceParams += `&utmSource=${encodeURIComponent(signupSource.utmSource)}`;
      }
      if (signupSource.utmMedium) {
        signupSourceParams += `&utmMedium=${encodeURIComponent(signupSource.utmMedium)}`;
      }
      if (signupSource.utmCampaign) {
        signupSourceParams += `&utmCampaign=${encodeURIComponent(signupSource.utmCampaign)}`;
      }
    }

    return this.http.post<{ notionUserId: string, widgetToken: string, created: boolean, connected?: boolean, email: string }>(
      `${environment.apiURL}/user/login/notion?code=${encodeURIComponent(code)}&timezone=${encodeURIComponent(timezone)}${gclidParam}${signupSourceParams}`,
      {},
      { observe: 'response' }
    ).pipe(
      tap((res: HttpResponse<{ notionUserId: string, widgetToken: string, created: boolean, connected?: boolean, email: string }>) => {
        const token = res.headers.get('Authorization');
        const body = res.body!;
        this.storageService.setItem('token', token);
        this.storageService.setItem('email', body.email);
        this.storageService.setItem('notionUserId', body.notionUserId);
        this.storageService.setItem('widgetToken', body.widgetToken);
        this.setLoggedIn(true);
        this.setLoggedInEmail(body.email);
        if (body.created === true) {
          this.gclidService.clear();
          this.signupSourceService.clear();
        }
      }),
      catchError((error) => {
        this.setLoggedIn(false);
        this.setLoggedInEmail('');
        return throwError(error);
      })
    );
  }

  getOauthToken(code: string, email: string): Observable<{ notionUserId: string, widgetToken: string }> {

    let timezone: string;
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return this.http.post<{ notionUserId: string, widgetToken: string }>(
      `${environment.apiURL}/user/oauth?code=${encodeURIComponent(code)}&email=${encodeURIComponent(email)}&timezone=${encodeURIComponent(timezone)}`,
      {},
      { responseType: 'json' }
    );
  }

  getOauthStatus(notionUserId: string): Observable<OauthStatus> {
    return this.http.get<OauthStatus>(
      `${environment.apiURL}/user/oauth/status?notionUserId=${encodeURIComponent(notionUserId)}`
    );
  }

  getBadges(notionUserId: string): Observable<UserBadgeDto[]> {
    return this.http.get<UserBadgeDto[]>(
      `${environment.apiURL}/user/badges?notionUserId=${encodeURIComponent(notionUserId)}`
    );
  }

  userHasAccessToken(email: string) {
    return this.http.get(`${environment.apiURL}/user/connected?email=${encodeURIComponent(email)}`, {}).pipe(
      timeout(5000),
      tap(() => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getNotionUserId(email: string) {
    return this.http.get<UserMeResponse>(`${environment.apiURL}/user/me?email=${encodeURIComponent(email)}`, {responseType: 'json'}).pipe(
      tap(() => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  /**
   * `GET /user/widget-token` without `notionUserId`: the backend resolves the
   * caller from the JWT principal (backend #366), the only path a NATIVE
   * account has. The token is minted on demand.
   */
  getWidgetToken(): Observable<WidgetTokenResponse> {
    return this.http.get<WidgetTokenResponse>(`${environment.apiURL}/user/widget-token`);
  }

  /**
   * `POST /user/timezone` with the browser's IANA zone. Sends `notionUserId`
   * when this browser has one (the connected case, unchanged); otherwise the
   * zone alone, and the backend resolves the caller from the JWT principal.
   * That principal fallback is backend #371 (!365): until it is deployed this
   * path 404s for an account with no `notionUserId`, which is fine because
   * its only caller, the native switch on /start, stays dark behind
   * NATIVE_TRACKING_ENABLED until then.
   */
  postBrowserTimezone(): Observable<void> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const notionUserId = this.storageService.getItem('notionUserId');
    const body: Record<string, string> = {timezone};
    if (notionUserId) {
      body['notionUserId'] = notionUserId;
    }
    return this.http.post<void>(`${environment.apiURL}/user/timezone`, body);
  }

  /**
   * Switches the account to native tracking (`POST /user/tracking-mode`,
   * backend #366). A 400 means the account has no timezone on file: post the
   * browser timezone and retry the switch exactly once. Every other error
   * (409 flag off, 403 unverified, a second 400) is propagated unchanged so
   * the caller can branch on the status. The returned widget token is stored
   * under `widgetToken`, like the connect success path; `notionUserId` is
   * left untouched.
   */
  switchToNativeTracking(): Observable<TrackingModeResponse> {
    return this.postTrackingModeNative().pipe(
      catchError((error) => {
        if (error?.status === 400) {
          return this.postBrowserTimezone().pipe(
            switchMap(() => this.postTrackingModeNative())
          );
        }
        return throwError(() => error);
      }),
      tap((response) => {
        if (response?.widgetToken) {
          this.storageService.setItem('widgetToken', response.widgetToken);
        }
      })
    );
  }

  private postTrackingModeNative(): Observable<TrackingModeResponse> {
    return this.http.post<TrackingModeResponse>(
      `${environment.apiURL}/user/tracking-mode`, {mode: 'NATIVE'}
    );
  }

  /**
   * Writes the `notionConn` snapshot. `trackerUrl` rule: an explicit value
   * (string or null) is stored as given; when omitted, a connected snapshot
   * keeps the previously stored one (callers that only know "still
   * connected" must not wipe the profile's tracker link) and a not-connected
   * snapshot never carries one.
   */
  setConnectionSnapshot(connected: boolean, reason: string | null, trackerUrl?: string | null): void {
    const url = !connected
      ? null
      : trackerUrl !== undefined
        ? trackerUrl
        : (this.getConnectionSnapshot()?.trackerUrl ?? null);
    // The key is only written when there is a link, so a snapshot without one keeps the old {connected, reason} shape.
    this.storageService.setItem('notionConn', url ? { connected, reason, trackerUrl: url } : { connected, reason });
  }

  getConnectionSnapshot(): { connected: boolean, reason: string | null, trackerUrl?: string | null } | null {
    const val = this.storageService.getItem('notionConn');
    if (val !== null && typeof val === 'object' && typeof val.connected === 'boolean') {
      return val as { connected: boolean, reason: string | null, trackerUrl?: string | null };
    }
    return null;
  }

  /** Back to "unknown": a failed refresh must not leave an earlier session's snapshot behind. */
  clearConnectionSnapshot(): void {
    this.storageService.removeItem('notionConn');
  }

  refreshConnectionSnapshot(email: string): Observable<void> {
    return this.getNotionUserId(email).pipe(
      switchMap(response => {
        if (response !== null) {
          if (response.notionUserId !== null && response.notionUserId !== undefined) {
            this.storageService.setItem('notionUserId', response.notionUserId);
          }
          if (response.widgetToken !== null && response.notionUserId !== undefined) {
            this.storageService.setItem('widgetToken', response.widgetToken);
          }
          if (response.notionUserId) {
            return this.getOauthStatus(response.notionUserId).pipe(
              tap(status => {
                if (status.connected === true) {
                  this.setConnectionSnapshot(true, null, notionTrackerUrl(status.trackerPageId, status.databaseId));
                } else if (status.error && status.reason) {
                  this.setConnectionSnapshot(false, status.reason);
                } else {
                  this.setConnectionSnapshot(false, null);
                }
              }),
              map(() => void 0),
              catchError(() => of(void 0))
            );
          } else {
            this.setConnectionSnapshot(false, null);
            return of(void 0);
          }
        } else {
          this.setConnectionSnapshot(false, null);
          return of(void 0);
        }
      }),
      catchError(() => of(void 0))
    );
  }

}
